import { ref, onMounted, onBeforeUnmount, nextTick, useSlots } from "vue";
import type { Ref } from "vue";

/**
 * A generic, typed composable that returns one boolean Ref per “slot key.”
 *
 * It checks:
 *   - Vue‐slot presence (useSlots()) if you’re in a “Vue component” context, and
 *   - Actual light‐DOM children (via MutationObserver on the CustomElement host)
 *     if you’re running as a Custom Element (Shadow DOM).
 *
 * @template Slots
 *   The interface whose keys represent your slot names, e.g.:
 *     interface ComponentSlots {
 *       header(props?: {}): any;
 *       default(props?: {}): any;
 *       footer(props?: {}): any;
 *     }
 *
 * @param elRef
 *   A `Ref<HTMLElement | null>` pointing to your component’s “root” element. In a normal
 *   Vue app, that’s whichever element you put `ref="elRef"` on (e.g. `<div ref="elRef">…</div>`).
 *   In a Custom Element build, that same `ref="elRef"` will live inside your ShadowRoot,
 *   so we detect the ShadowRoot and switch to observing its `.host` (the actual custom‐element tag).
 *
 * @param slotKeys
 *   An array of strings, each one must be a key of `Slots & string`. If you want to detect
 *   the default (unnamed) slot, use exactly the string `"default"`.
 *
 * @returns
 *   A typed object `{ [K in keyof Slots]: Ref<boolean> }`. Each boolean flips to `true`
 *   if either (a) Vue sees a slot function (useSlots()) defined for that key, or (b) the Custom Element
 *   host has light‐DOM children matching that slot name.
 */
/** Resolve the custom element host from an inner element ref. */
const getHostElement = (elRef: Ref<HTMLElement | null>): HTMLElement | null => {
  const inner = elRef.value;
  if (!inner) return null;
  const rootNode = inner.getRootNode();
  return rootNode instanceof ShadowRoot ? (rootNode.host as HTMLElement) : inner;
};

/** Check if the host has default-slot light DOM children (text or unslotted elements). */
const hasDefaultDomSlot = (hostEl: HTMLElement): boolean => {
  for (const node of hostEl.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && (node.textContent?.trim() ?? "") !== "") return true;
    if (node.nodeType === Node.ELEMENT_NODE && !(node as HTMLElement).hasAttribute("slot")) return true;
  }
  return false;
};

/** Check if the host has a named or default DOM slot child. */
const hasDomSlot = (hostEl: HTMLElement | null, key: string): boolean => {
  if (!hostEl) return false;
  if (key === "default") return hasDefaultDomSlot(hostEl);
  return hostEl.querySelector(`[slot="${key}"]`) !== null;
};

export const useConditionalSlots = <Slots extends Record<string, unknown>>(
  elRef: Ref<HTMLElement | null>,
  slotKeys: Array<keyof Slots & string>,
): { [K in keyof Slots]: Ref<boolean> } => {
  const conditionMap = {} as { [K in keyof Slots]: Ref<boolean> };
  for (const key of slotKeys) {
    conditionMap[key as keyof Slots] = ref(false) as Ref<boolean>;
  }

  const vueSlots = useSlots();
  let observer: MutationObserver;

  const hasVueSlot = (key: string): boolean => {
    if (key === "default") return !!vueSlots.default;
    return typeof vueSlots[key] === "function";
  };

  const updateAllSlots = () => {
    const hostEl = getHostElement(elRef);
    for (const key of slotKeys) {
      conditionMap[key as keyof Slots].value = hasVueSlot(key) || hasDomSlot(hostEl, key);
    }
  };

  onMounted(() => {
    nextTick(() => updateAllSlots());
    const hostEl = getHostElement(elRef);
    if (!hostEl) return;
    observer = new MutationObserver(() => updateAllSlots());
    observer.observe(hostEl, { childList: true });
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
  });

  return conditionMap;
};
